import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '@akillidernek/api-client'
import { Button, Card, CardBody, CardHeader, Field, Input } from '@akillidernek/ui'
import { api } from '../api'
import { type Dernek } from '../use-dernek'

type Alan = Dernek['application_fields'][number]

const girdiSinifi =
  'h-9 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm'

/**
 * Üyelik başvurusu — YES-159.
 *
 * Form derneğin kendi özel alanlarını (YES-105) da taşır; hangi alanların
 * sorulacağını sunucu söyler, burada sabit bir liste yok. Başvuru `pending`
 * açılır: kabul yönetim kurulunun kararıdır.
 */
export function Basvuru({
  dernek,
  girisli,
  onGiris,
  onTamam,
}: {
  dernek: Dernek
  girisli: boolean
  onGiris: () => void
  onTamam: () => void
}) {
  const [hata, setHata] = useState<string | null>(null)
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({})

  const basvur = useMutation({
    mutationFn: async (form: FormData) => {
      const oku = (ad: string) => String(form.get(ad) ?? '').trim() || null
      const ozel: Record<string, unknown> = {}
      for (const alan of dernek.application_fields) {
        const ham = form.get(`ozel_${alan.key}`)
        if (alan.field_type === 'boolean') ozel[alan.key] = ham !== null
        else if (ham !== null && String(ham).trim() !== '') ozel[alan.key] = String(ham).trim()
      }
      await api.POST('/api/v1/portal/applications', {
        body: {
          first_name: String(form.get('first_name') ?? '').trim(),
          last_name: String(form.get('last_name') ?? '').trim(),
          email: String(form.get('email') ?? '').trim(),
          // Girişliyse şifre gönderilmez: hesabı zaten var.
          password: girisli ? null : String(form.get('password') ?? ''),
          phone: oku('phone'),
          national_id: oku('national_id'),
          birth_date: oku('birth_date'),
          membership_type_id: String(form.get('membership_type_id')),
          custom_fields: ozel,
        },
      })
    },
    onSuccess: onTamam,
    onError: (error) => {
      if (error instanceof ApiError) {
        setHata(error.problem.detail)
        setAlanHatalari(error.fieldErrors())
      } else setHata('Beklenmeyen bir hata oluştu.')
    },
  })

  // Üyelik tipi olmayan dernek başvuru alamaz: boş bir açılır liste gösterip
  // "gönder"de hata vermektense burada dürüst olmak daha iyi.
  if (dernek.membership_types.length === 0) {
    return (
      <Card className="w-[min(30rem,100%)]">
        <CardHeader
          title="Başvuru alınamıyor"
          description={`${dernek.name} portaldan üyelik başvurusu almıyor.`}
        />
        <CardBody className="flex flex-col gap-3 text-sm">
          <p>
            {dernek.contact_email
              ? `Üyelik için ${dernek.contact_email} adresinden dernekle iletişime geçin.`
              : 'Üyelik için dernekle iletişime geçin.'}
          </p>
          <Button variant="secondary" onClick={onGiris}>
            Girişe dön
          </Button>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="w-[min(40rem,100%)]">
      <CardHeader
        title="Üyelik başvurusu"
        description={`${dernek.name} üyeliği için başvurunuzu yönetim kurulu değerlendirir.`}
      />
      <CardBody>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            setHata(null)
            setAlanHatalari({})
            basvur.mutate(new FormData(event.currentTarget))
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Ad" required error={alanHatalari.first_name}>
              {(props) => <Input name="first_name" autoComplete="given-name" {...props} />}
            </Field>
            <Field label="Soyad" required error={alanHatalari.last_name}>
              {(props) => <Input name="last_name" autoComplete="family-name" {...props} />}
            </Field>
            <Field label="E-posta" required error={alanHatalari.email}>
              {(props) => <Input name="email" type="email" autoComplete="email" {...props} />}
            </Field>
            <Field label="Telefon" error={alanHatalari.phone}>
              {(props) => <Input name="phone" inputMode="tel" autoComplete="tel" {...props} />}
            </Field>
            <Field
              label="T.C. Kimlik No"
              hint="Dernek kaydı için gerekir; yalnızca yetkili görür."
              error={alanHatalari.national_id}
            >
              {(props) => (
                <Input name="national_id" inputMode="numeric" maxLength={11} {...props} />
              )}
            </Field>
            <Field label="Doğum tarihi" error={alanHatalari.birth_date}>
              {(props) => <Input name="birth_date" type="date" {...props} />}
            </Field>
          </div>

          <Field label="Üyelik tipi" required error={alanHatalari.membership_type_id}>
            {({ id }) => (
              <select id={id} name="membership_type_id" required className={girdiSinifi}>
                {dernek.membership_types.map((tip) => (
                  <option key={tip.id} value={tip.id}>
                    {tip.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {dernek.application_fields.length > 0 && (
            <fieldset className="flex flex-col gap-3">
              <legend className="pb-1 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                {dernek.name} soruları
              </legend>
              {dernek.application_fields.map((alan) => (
                <OzelAlan key={alan.key} alan={alan} hata={alanHatalari[alan.key]} />
              ))}
            </fieldset>
          )}

          {!girisli && (
            <Field
              label="Şifre"
              required
              hint="En az 12 karakter. Başvurunuzun durumunu bu şifreyle takip edeceksiniz."
              error={alanHatalari.password}
            >
              {(props) => (
                <Input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  {...props}
                />
              )}
            </Field>
          )}

          {hata && (
            <p role="alert" className="text-xs text-[var(--ad-danger)]">
              {hata}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            {!girisli ? (
              <Button type="button" variant="ghost" size="sm" onClick={onGiris}>
                Zaten hesabım var
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" disabled={basvur.isPending}>
              {basvur.isPending ? 'Gönderiliyor…' : 'Başvuruyu gönder'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}

function OzelAlan({ alan, hata }: { alan: Alan; hata?: string }) {
  const ad = `ozel_${alan.key}`
  if (alan.field_type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" name={ad} />
        {alan.label}
        {hata && <span className="text-xs text-[var(--ad-danger)]">{hata}</span>}
      </label>
    )
  }
  return (
    <Field label={alan.label} required={alan.is_required} error={hata}>
      {(props) =>
        alan.field_type === 'select' || alan.field_type === 'multi_select' ? (
          <select
            id={props.id}
            name={ad}
            required={alan.is_required}
            multiple={alan.field_type === 'multi_select'}
            className={girdiSinifi}
          >
            {!alan.is_required && <option value="">Seçilmedi</option>}
            {alan.options.map((secenek) => {
              const deger = String(secenek.value ?? '')
              return (
                <option key={deger} value={deger}>
                  {String(secenek.label ?? deger)}
                </option>
              )
            })}
          </select>
        ) : alan.field_type === 'long_text' ? (
          <textarea
            id={props.id}
            name={ad}
            required={alan.is_required}
            rows={3}
            className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
          />
        ) : (
          <Input
            name={ad}
            type={
              alan.field_type === 'number' ? 'number' : alan.field_type === 'date' ? 'date' : 'text'
            }
            {...props}
          />
        )
      }
    </Field>
  )
}
